import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { IoSearch } from 'react-icons/io5'
import { CalendarIcon } from '@heroicons/react/outline'
import { DateRangePicker } from 'react-date-range'
import { defaultInputRanges, defaultStaticRanges } from 'react-date-range/dist/defaultRanges'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { arSA, enUS } from 'date-fns/locale'
import { get, includes, isArray, isEmpty, isNil, join, map, pickBy, some } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import Table from 'components/pageComponents/Table'
import EmptyState from 'components/pageComponents/EmptyState'
import LoadingIcon from 'components/atoms/LoadingIcon'
import Modal from 'components/molecules/Modal'
import Popover from 'components/molecules/Popover'
import PageFooter from 'components/paginationComponent'
import {
  Badge,
  Breakdown,
  Field,
  InfoList,
  MethodBadge,
  NumberInput,
  REFUND_STATUSES,
  REFUND_TYPES,
  ReasonField,
  Select,
  StatusBadge,
  btn,
  fmtDate,
  inputClass,
  isBlank,
  omr,
  toNumber,
} from './refundShared'

const FINAL_STATUSES = ['credited', 'rejected', 'no_refund']
const PAGE_SIZE = 20
const MODAL_DIMENSIONS = { width: '92vw', maxWidth: '720px', height: 'auto', maxHeight: '85vh' }

/** Search box that applies on Enter (or when cleared), like the old Input.Search. */
const SearchInput = ({ placeholder, onSearch }) => {
  const [value, setValue] = useState('')
  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center px-3">
        <IoSearch className="h-4 w-4 text-gray-400" aria-hidden="true" />
      </div>
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={e => {
          setValue(e.target.value)
          if (!e.target.value) onSearch('')
        }}
        onKeyDown={e => e.key === 'Enter' && onSearch(value)}
        className={`${inputClass()} pl-9 rtl:pl-3 rtl:pr-9`}
      />
    </div>
  )
}

const STATIC_RANGE_KEYS = {
  Today: 'today',
  Yesterday: 'yesterday',
  'This Week': 'thisWeek',
  'Last Week': 'lastWeek',
  'This Month': 'thisMonth',
  'Last Month': 'lastMonth',
}
const INPUT_RANGE_KEYS = { 'days up to today': 'daysUpToToday', 'days starting today': 'daysStartingToday' }

/** One button + popover range picker (same picker as the other report pages). `value` is [from, to] as YYYY-MM-DD. */
const DateRangeFilter = ({ value, onChange }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [from, to] = value || []
  const staticRanges = useMemo(
    () => map(defaultStaticRanges, r => ({ ...r, label: STATIC_RANGE_KEYS[r.label] ? t(`reports.datePicker.${STATIC_RANGE_KEYS[r.label]}`) : r.label })),
    [t]
  )
  const inputRanges = useMemo(() => map(defaultInputRanges, r => ({ ...r, label: t(`reports.datePicker.${INPUT_RANGE_KEYS[r.label]}`, r.label) })), [t])
  const selection = { startDate: from ? moment(from).toDate() : new Date(), endDate: to ? moment(to).toDate() : new Date(), key: 'selection' }
  const label = from && to ? `${moment(from).format('MMM D, YYYY')} – ${moment(to).format('MMM D, YYYY')}` : t('reports.filters.selectDateRange')

  return (
    <Popover
      custom
      placement="bottom"
      buttonClassName="block w-full"
      content={close => (
        <DateRangePicker
          weekStartsOn={0}
          locale={isRTL ? arSA : enUS}
          staticRanges={staticRanges}
          inputRanges={inputRanges}
          ranges={[selection]}
          onChange={ranges => {
            const next = ranges?.selection
            if (next?.startDate && next?.endDate) onChange([moment(next.startDate).format('YYYY-MM-DD'), moment(next.endDate).format('YYYY-MM-DD')])
          }}
          footerContent={
            <div className="flex gap-2 p-3">
              <button type="button" className={btn('secondary', true)} onClick={close}>
                {t('reports.datePicker.close')}
              </button>
              <button
                type="button"
                className={btn('secondary', true)}
                disabled={!from && !to}
                onClick={() => {
                  onChange(undefined)
                  close()
                }}
              >
                {t('reports.datePicker.clear')}
              </button>
            </div>
          }
        />
      )}
    >
      <span className={`${inputClass()} flex items-center gap-2 bg-white text-left rtl:text-right`}>
        <CalendarIcon className="h-4 w-4 text-gray-400 shrink-0" aria-hidden="true" />
        <span className={`truncate ${from && to ? 'text-gray-900' : 'text-gray-500'}`}>{label}</span>
      </span>
    </Popover>
  )
}

const RefundsList = ({ onChanged }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'ar' ? 'ar' : 'en'
  const [filters, setFilters] = useState({})
  // Bumped by "Clear filters" to remount the search boxes with empty text.
  const [searchKey, setSearchKey] = useState(0)
  const [data, setData] = useState({ list: [], pagination: {} })
  const [loading, setLoading] = useState(false)
  const [ruleCodes, setRuleCodes] = useState([])
  const [details, setDetails] = useState(null)
  const [action, setAction] = useState(null) // { type: 'reject' | 'override', refund }
  const [form, setForm] = useState({})
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const fetchRefunds = async (page = 1) => {
    if (page === 1) setLoading(true)
    const { status, dates, ...rest } = filters
    const params = pickBy(
      {
        ...rest,
        page,
        size: PAGE_SIZE,
        status: isEmpty(status) ? undefined : join(status, ','),
        sd: dates?.[0] ? moment(dates[0]).startOf('day').toISOString() : undefined,
        ed: dates?.[1] ? moment(dates[1]).endOf('day').toISOString() : undefined,
      },
      value => value !== undefined && value !== ''
    )
    const response = await apiGet('refunds', params)
    setLoading(false)
    if (response?.success) {
      setData(prev => ({ list: page === 1 ? response.data.list || [] : [...prev.list, ...(response.data.list || [])], pagination: response.data.pagination || {} }))
    }
  }

  useEffect(() => {
    fetchRefunds(1)
  }, [filters])

  useEffect(() => {
    apiGet('refunds/rule-codes').then(response => response?.success && setRuleCodes(response.data || []))
  }, [])

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  const hasFilters = some(filters, v => (isArray(v) ? some(v) : !isNil(v) && v !== ''))
  const clearFilters = () => {
    setFilters({})
    setSearchKey(k => k + 1)
  }

  const refresh = () => {
    fetchRefunds(1)
    onChanged && onChanged()
  }

  const openDetails = async refund => {
    const response = await apiGet(`refunds/${refund._id}`)
    if (response?.success) setDetails(response.data)
  }

  const approve = refund =>
    notify.alertConfirm({
      title: t('refundEngine.refunds.approveConfirm', { amount: omr(refund.finalAmount) }),
      dangerText: t('refundEngine.refunds.approve'),
      cancelText: t('refundEngine.common.cancel'),
      onConfirm: async () => {
        const response = await apiPost(`refunds/${refund._id}/approve`, {})
        if (response?.success) {
          notify.success(t('refundEngine.refunds.approved'))
          refresh()
        }
      },
    })

  const openAction = (type, refund) => {
    setForm({})
    setErrors({})
    setAction({ type, refund })
  }

  const maxOverride = get(action, 'refund.breakdown.paidTotal')

  const submitAction = async () => {
    const { type, refund } = action
    const nextErrors = {}
    if (isBlank(form.reason)) nextErrors.reason = t('refundEngine.common.required')
    if (type === 'override') {
      const amount = toNumber(form.amount)
      if (isNil(amount)) nextErrors.amount = t('refundEngine.common.required')
      else if (amount < 0 || (!isNil(maxOverride) && amount > maxOverride))
        nextErrors.amount = t('refundEngine.refunds.overrideHint', { calculated: omr(refund.calculatedAmount), max: omr(maxOverride) })
    }
    setErrors(nextErrors)
    if (!isEmpty(nextErrors)) return
    const values = type === 'override' ? { amount: toNumber(form.amount), reason: form.reason } : { reason: form.reason }
    setSubmitting(true)
    const response = await apiPost(`refunds/${refund._id}/${type}`, values)
    setSubmitting(false)
    if (response?.success) {
      notify.success(t(`refundEngine.refunds.${type === 'reject' ? 'rejected' : 'overridden'}`))
      setAction(null)
      refresh()
    }
  }

  const who = person => get(person, 'fullName', '-')
  const amountCell = value => <div className="text-right rtl:text-left tabular-nums">{omr(value)}</div>

  const columns = [
    {
      name: t('refundEngine.refunds.columns.booking'),
      rendered: r =>
        r.booking ? (
          <Link to={`/booking/${r.booking._id}`} className="font-medium text-primary-600 hover:underline">
            {r.booking.bookingReferenceId || r.booking._id}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      name: t('refundEngine.refunds.columns.customer'),
      rendered: r => (
        <div>
          <div className="font-medium text-gray-900">{get(r, 'user.fullName', '-')}</div>
          <div className="text-xs text-gray-500">{get(r, 'user.email') || get(r, 'user.phone', '')}</div>
        </div>
      ),
    },
    {
      name: t('refundEngine.refunds.columns.rule'),
      rendered: r => (
        <div>
          <span className="font-semibold text-gray-900">{r.ruleCode || '-'}</span> {r.ruleVersion ? <span className="text-xs text-gray-500">v{r.ruleVersion}</span> : ''}
          <div className="text-xs text-gray-500">{get(r, `ruleSnapshot.name.${lang}`, '')}</div>
        </div>
      ),
    },
    { name: t('refundEngine.refunds.columns.refundType'), rendered: r => (r.refundType ? t(`refundEngine.refundType.${r.refundType}`) : '-') },
    { name: t('refundEngine.refunds.columns.calculated'), rendered: r => amountCell(r.calculatedAmount) },
    {
      name: t('refundEngine.refunds.columns.final'),
      rendered: r => (
        <div className="flex items-center justify-end gap-2">
          {r.override && <Badge tone="purple">{t('refundEngine.refunds.overriddenTag')}</Badge>}
          <span className="font-semibold tabular-nums">{omr(r.finalAmount)}</span>
        </div>
      ),
    },
    { name: t('refundEngine.refunds.columns.status'), rendered: r => <StatusBadge status={r.status} /> },
    { name: t('refundEngine.refunds.columns.method'), rendered: r => <MethodBadge method={r.method} /> },
    {
      name: t('refundEngine.refunds.columns.slaDue'),
      rendered: r => {
        const late = r.slaDueAt && !includes(FINAL_STATUSES, r.status) && moment(r.slaDueAt).isBefore()
        return <span className={late ? 'text-red-600 font-medium' : 'text-gray-700'}>{fmtDate(r.slaDueAt)}</span>
      },
    },
    { name: t('refundEngine.refunds.columns.created'), rendered: r => <span className="text-gray-500">{fmtDate(r.createdAt)}</span> },
    {
      name: t('refundEngine.common.actions'),
      rendered: r => (
        <div className="flex items-center gap-2">
          <button type="button" className={btn('secondary', true)} onClick={() => openDetails(r)}>
            {t('refundEngine.common.details')}
          </button>
          {r.status === 'pending_approval' && (
            <>
              <button type="button" className={btn('primary', true)} onClick={() => approve(r)}>
                {t('refundEngine.refunds.approve')}
              </button>
              <button type="button" className={btn('danger', true)} onClick={() => openAction('reject', r)}>
                {t('refundEngine.refunds.reject')}
              </button>
              <button type="button" className={btn('secondary', true)} onClick={() => openAction('override', r)}>
                {t('refundEngine.refunds.override')}
              </button>
            </>
          )}
        </div>
      ),
    },
  ]

  const { pagination } = data

  return (
    <div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200 space-y-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-3">
            <SearchInput key={`booking-${searchKey}`} placeholder={t('refundEngine.refunds.filters.booking')} onSearch={v => setFilter('booking', v)} />
          </div>
          <div className="lg:col-span-4">
            <SearchInput key={`customer-${searchKey}`} placeholder={t('refundEngine.refunds.filters.customer')} onSearch={v => setFilter('customer', v)} />
          </div>
          <div className="lg:col-span-5">
            <DateRangeFilter value={filters.dates} onChange={v => setFilter('dates', v)} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-center">
          <div className="lg:col-span-3">
            <Select
              multiple
              value={filters.status}
              onChange={v => setFilter('status', v)}
              placeholder={t('refundEngine.refunds.filters.status')}
              options={map(REFUND_STATUSES, s => ({ value: s, label: t(`refundEngine.status.${s}`) }))}
            />
          </div>
          <div className="lg:col-span-3">
            <Select
              clearable
              showSearch
              value={filters.ruleCode}
              onChange={v => setFilter('ruleCode', v)}
              placeholder={t('refundEngine.refunds.filters.rule')}
              options={map(ruleCodes, c => ({ value: c, label: c }))}
            />
          </div>
          <div className="lg:col-span-2">
            <Select
              clearable
              value={filters.refundType}
              onChange={v => setFilter('refundType', v)}
              placeholder={t('refundEngine.refunds.filters.refundType')}
              options={map(REFUND_TYPES, v => ({ value: v, label: t(`refundEngine.refundType.${v}`) }))}
            />
          </div>
          <div className="lg:col-span-2">
            <Select
              clearable
              value={filters.requiresApproval}
              onChange={v => setFilter('requiresApproval', v)}
              placeholder={t('refundEngine.refunds.filters.requiresApproval')}
              options={[
                { value: 'true', label: t('refundEngine.common.yes') },
                { value: 'false', label: t('refundEngine.common.no') },
              ]}
            />
          </div>
          <div className="lg:col-span-2 flex lg:justify-end">
            <button type="button" className={btn('secondary')} onClick={clearFilters} disabled={!hasFilters}>
              {t('refundEngine.refunds.filters.clear')}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingIcon />
        </div>
      ) : data.list.length === 0 ? (
        <EmptyState v2 description={t('common.noData')} />
      ) : (
        <Table
          compact
          hasMore={(pagination.currentPage || 1) < (pagination.totalPages || 1)}
          fetchData={() => fetchRefunds((pagination.currentPage || 1) + 1)}
          data={data.list}
          columns={columns}
        />
      )}
      <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />

      <Modal
        open={Boolean(action)}
        closeable
        onClose={() => setAction(null)}
        title={action ? `${t(`refundEngine.refunds.${action.type}`)} — ${get(action, 'refund.booking.bookingReferenceId', '')}` : ''}
        dimensions={{ width: '92vw', maxWidth: '520px', height: 'auto' }}
      >
        {action && (
          <div className="space-y-4">
            {action.type === 'override' && (
              <Field
                label={t('refundEngine.refunds.overrideAmount')}
                htmlFor="override-amount"
                error={errors.amount}
                hint={t('refundEngine.refunds.overrideHint', { calculated: omr(action.refund.calculatedAmount), max: omr(maxOverride) })}
              >
                <NumberInput
                  id="override-amount"
                  min={0}
                  max={maxOverride}
                  step={0.001}
                  addon="OMR"
                  value={form.amount}
                  onChange={v => setForm(prev => ({ ...prev, amount: v }))}
                  error={errors.amount}
                />
              </Field>
            )}
            <ReasonField label={t('refundEngine.refunds.reason')} value={form.reason} onChange={v => setForm(prev => ({ ...prev, reason: v }))} error={errors.reason} />
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button type="button" className={btn('secondary')} onClick={() => setAction(null)} disabled={submitting}>
                {t('refundEngine.common.cancel')}
              </button>
              <button type="button" className={btn(action.type === 'reject' ? 'danger' : 'primary')} onClick={submitAction} disabled={submitting}>
                {submitting ? t('common.saving') : t('refundEngine.common.confirm')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(details)} closeable onClose={() => setDetails(null)} title={t('refundEngine.refunds.detailsTitle')} dimensions={MODAL_DIMENSIONS}>
        {details && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-4">
              <div>
                <p className="text-xs text-gray-500">{get(details, 'booking.bookingReferenceId', '-')}</p>
                <p className="text-2xl font-semibold text-primary-600 tabular-nums">{omr(details.finalAmount)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={details.status} />
                <MethodBadge method={details.method} />
                {details.override && <Badge tone="purple">{t('refundEngine.refunds.overriddenTag')}</Badge>}
              </div>
            </div>
            <InfoList
              rows={[
                { label: t('refundEngine.refunds.columns.booking'), value: get(details, 'booking.bookingReferenceId', '-') },
                {
                  label: t('refundEngine.refunds.columns.customer'),
                  value: `${get(details, 'user.fullName', '-')} · ${get(details, 'user.email', '')} ${get(details, 'user.phone', '')}`,
                },
                { label: t('refundEngine.refunds.kind'), value: details.kind ? t(`refundEngine.kind.${details.kind}`) : '-' },
                {
                  label: t('refundEngine.refunds.columns.rule'),
                  value: `${details.ruleCode || ''} ${details.ruleVersion ? `v${details.ruleVersion}` : ''} ${get(details, `ruleSnapshot.name.${lang}`, '')}`,
                },
                { label: t('refundEngine.rules.fields.customerText'), value: get(details, `ruleSnapshot.customerText.${lang}`, '-') },
                { label: t('refundEngine.refunds.columns.refundType'), value: details.refundType ? t(`refundEngine.refundType.${details.refundType}`) : '-' },
                { label: t('refundEngine.facts.reason'), value: details.reason || '-' },
                { label: t('refundEngine.facts.cancelledBy'), value: details.cancelledBy ? t(`refundEngine.cancelledBy.${details.cancelledBy}`) : '-' },
                { label: t('refundEngine.refunds.columns.calculated'), value: <span className="tabular-nums">{omr(details.calculatedAmount)}</span> },
                { label: t('refundEngine.refunds.columns.final'), value: <span className="font-semibold tabular-nums">{omr(details.finalAmount)}</span> },
                details.override && {
                  label: t('refundEngine.refunds.override'),
                  value: `${omr(details.override.amount)} — ${details.override.reason} (${who(details.override.by)}, ${fmtDate(details.override.at)})`,
                },
                details.destination && {
                  label: t('refundEngine.refunds.destination'),
                  value: `${t('refundEngine.method.gateway')}: ${omr(get(details, 'destination.paymob', 0))} · ${t('refundEngine.method.wallet')}: ${omr(
                    get(details, 'destination.wallet', 0)
                  )}`,
                },
                !isEmpty(details.gatewayRefunds) && {
                  label: t('refundEngine.refunds.gatewayRefunds'),
                  value: <pre className="text-xs text-left whitespace-pre-wrap bg-gray-50 rounded p-2">{JSON.stringify(details.gatewayRefunds, null, 2)}</pre>,
                },
                { label: t('refundEngine.refunds.requestedBy'), value: who(details.requestedBy) },
                { label: t('refundEngine.refunds.approvedBy'), value: `${who(details.approvedBy)} ${details.approvedAt ? `· ${fmtDate(details.approvedAt)}` : ''}` },
                {
                  label: t('refundEngine.refunds.rejectedBy'),
                  value: `${who(details.rejectedBy)} ${details.rejectedAt ? `· ${fmtDate(details.rejectedAt)}` : ''} ${
                    details.rejectionReason ? `— ${details.rejectionReason}` : ''
                  }`,
                },
                { label: t('refundEngine.refunds.processedBy'), value: who(details.processedBy) },
                { label: t('refundEngine.refunds.creditedAt'), value: fmtDate(details.creditedAt) },
                { label: t('refundEngine.refunds.cancelledAt'), value: fmtDate(details.cancelledAt) },
                { label: t('refundEngine.refunds.columns.slaDue'), value: fmtDate(details.slaDueAt) },
                { label: t('refundEngine.refunds.columns.created'), value: fmtDate(details.createdAt) },
              ]}
            />
            <Breakdown breakdown={details.breakdown} />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default RefundsList
